import React, { useState, useRef } from 'react';

const ResumeUploadSection = ({ onUploadComplete, usageData, onError }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    // Validate file type
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      onError('Please upload a PDF or DOCX file');
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      onError('File size exceeds 5MB limit');
      return;
    }

    setSelectedFile(file);
    onError(null);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      onError('Please select a file first');
      return;
    }

    // Check if user has reviews remaining
    if (usageData && !usageData.unlimited_access && usageData.reviews_remaining <= 0) {
      onError('You have used all your reviews for this month. Contact admin for unlimited access.');
      return;
    }

    setUploading(true);
    setProgress(0);
    setStatusMessage('Uploading resume...');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please log in to continue');
      }

      const formData = new FormData();
      formData.append('file', selectedFile);

      // Multi-stage progress simulation
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          // Stage 1: Quick progress to 30% (uploading)
          if (prev < 30) {
            return prev + 10;
          }
          // Stage 2: Moderate progress to 60% (extracting text)
          if (prev < 60) {
            return prev + 5;
          }
          // Stage 3: Slower progress to 90% (AI analysis starting)
          if (prev < 90) {
            return prev + 3;
          }
          // Stage 4: Very slow progress after 90% (AI analyzing) - never reaches 100%
          if (prev < 98) {
            return prev + 0.5;
          }
          return prev;
        });
      }, 500);

      // Update status messages at key milestones
      setTimeout(() => setStatusMessage('Extracting text from resume...'), 2000);
      setTimeout(() => setStatusMessage('AI is analyzing your resume...'), 4000);
      setTimeout(() => setStatusMessage('Generating personalized feedback...'), 8000);
      setTimeout(() => setStatusMessage('Almost done, finalizing results...'), 12000);

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/alumni/resume/upload-for-review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      clearInterval(progressInterval);
      setProgress(100);
      setStatusMessage('Complete!');

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const reviewData = await response.json();

      // Success! Pass data back to parent
      setTimeout(() => {
        onUploadComplete(reviewData);
        setSelectedFile(null);
        setProgress(0);
      }, 500);

    } catch (err) {
      console.error('Upload error:', err);
      onError(err.message || 'Failed to upload resume. Please try again.');
      setProgress(0);
      setStatusMessage('');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setProgress(0);
    setStatusMessage('');
    onError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Upload Your Resume
        </h2>
        <p className="text-gray-600 mb-6">
          Upload your resume in PDF or DOCX format for analysis and feedback.
        </p>

        {/* Drag and Drop Zone */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-12 text-center transition-all duration-200 ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : selectedFile
              ? 'border-green-500 bg-green-50'
              : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          }`}
        >
          {selectedFile ? (
            <div>
              <svg className="mx-auto h-16 w-16 text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-medium text-gray-900 mb-2">
                {selectedFile.name}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
              <button
                onClick={handleCancel}
                disabled={uploading}
                className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
              >
                Remove file
              </button>
            </div>
          ) : (
            <div>
              <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-lg font-medium text-gray-900 mb-2">
                Drop your resume here
              </p>
              <p className="text-sm text-gray-500 mb-4">
                or click to browse
              </p>
              <p className="text-xs text-gray-400">
                PDF or DOCX • Max 5MB
              </p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx"
            onChange={handleFileChange}
            className="hidden"
            disabled={uploading}
          />

          {!selectedFile && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="mt-4 px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Choose File
            </button>
          )}
        </div>

        {/* Progress Bar */}
        {uploading && (
          <div className="mt-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span className="font-medium flex items-center">
                {statusMessage}
                <span className="inline-flex ml-1">
                  <span className="animate-pulse">.</span>
                  <span className="animate-pulse animation-delay-200">.</span>
                  <span className="animate-pulse animation-delay-400">.</span>
                </span>
              </span>
              <span className="font-semibold">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
              <div
                className="bg-gradient-to-r from-blue-500 to-indigo-500 h-3 rounded-full transition-all duration-500 ease-out shadow-md"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs text-blue-700 text-center">
                <span className="font-semibold">⏳ Processing your resume...</span>
                {progress < 60 && " This typically takes 5-20 seconds."}
                {progress >= 60 && progress < 90 && " AI is analyzing your content..."}
                {progress >= 90 && " Almost there! Finalizing your personalized feedback..."}
              </p>
            </div>
          </div>
        )}

        {/* Upload Button */}
        {selectedFile && !uploading && (
          <div className="mt-6">
            <button
              onClick={handleUpload}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Start AI Review
            </button>
          </div>
        )}

        {/* Info Cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-blue-600 font-semibold mb-1">Fast Analysis</div>
            <div className="text-sm text-gray-600">Results in 5-10 seconds</div>
          </div>
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="text-indigo-600 font-semibold mb-1">AI-Powered</div>
            <div className="text-sm text-gray-600"></div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-purple-600 font-semibold mb-1">Actionable Tips</div>
            <div className="text-sm text-gray-600">8-10 specific recommendations</div>
          </div>
        </div>

        {/* Requirements */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">Requirements:</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-start">
              <svg className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              PDF or DOCX format only
            </li>
            <li className="flex items-start">
              <svg className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Maximum file size: 5MB
            </li>
            <li className="flex items-start">
              <svg className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Text-based resume (not image-only PDFs)
            </li>
            <li className="flex items-start">
              <svg className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Rate limit: 1 review per 30 seconds
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ResumeUploadSection;
